import React, { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import Markdown from 'components/Markdown';
import type { types as AuthTypes } from 'modules/Auth';
import Auth from 'modules/Auth';
import { getTypographyForMarkdown } from 'lib/getTypographyForMarkdown';
import { TypographyVariant } from 'components/Typography';
import { useMultiFactorLogin } from '../../hooks';
import { type MultiFactorAuthFormFields } from '../../types';
import { MINUTE } from '../../constants';
import ResendCodeButton from './components/ResendCodeButton';
import './resendCodeButtons.css';

const { MULTI_FACTOR_FAILURE_TYPE, MULTI_FACTOR_METHODS } = Auth.constants;
const typographyForMarkdown = getTypographyForMarkdown(TypographyVariant.BodyXxs);

export const ResendCodeButtons = () => {
  const { reset, clearErrors, setError } = useFormContext<MultiFactorAuthFormFields>();
  const { makeLoginRequest, error: loginError, multiFactorAuthentication } = useMultiFactorLogin();

  const { mfaCodeDeliveredTo, mfaFailureType, redactedPhoneNumber, email } =
    multiFactorAuthentication;

  const handleResendMultiFactorAuthCode = useCallback(
    async (method: AuthTypes.MultiFactorAuthMethodType) => {
      const params = new FormData();

      params.append('mfaCodeDeliveryMethod', method);
      try {
        await makeLoginRequest(params);
      } catch (err: unknown) {
        const typedError = err as {
          success: boolean;
          message: string;
          data?: { mfaFailureType: string };
        };

        if (typedError?.data?.mfaFailureType === MULTI_FACTOR_FAILURE_TYPE.MFA_REQUIRED) {
          // Success response is returned as error from BE
          reset();
          clearErrors();
        } else if (!typedError.success && !typedError?.data?.mfaFailureType) {
          setError('root.serverError', {
            type: 'manual',
            message: typedError.message
          });
        }
      }
    },
    [makeLoginRequest, reset, clearErrors, setError]
  );

  const handleResendSMS = useCallback(() => {
    void handleResendMultiFactorAuthCode(MULTI_FACTOR_METHODS.SMS);
  }, [handleResendMultiFactorAuthCode]);

  const handleResendEmail = useCallback(() => {
    void handleResendMultiFactorAuthCode(MULTI_FACTOR_METHODS.EMAIL);
  }, [handleResendMultiFactorAuthCode]);

  if (mfaFailureType === MULTI_FACTOR_FAILURE_TYPE.TOO_MANY_FAILED_MFA_CODE_CHECKS) return null;

  return (
    <div className="resendCodeButtons__container">
      <div className="resendCodeButtons__container--locked">
        {mfaFailureType === MULTI_FACTOR_FAILURE_TYPE.TOO_MANY_MFA_CODES_SENT && (
          <Markdown content={loginError ?? ''} renderers={typographyForMarkdown} />
        )}
      </div>

      {mfaFailureType !== MULTI_FACTOR_FAILURE_TYPE.TOO_MANY_MFA_CODES_SENT && (
        <>
          {redactedPhoneNumber && redactedPhoneNumber.length > 0 && (
            <ResendCodeButton
              contactMethod={MULTI_FACTOR_METHODS.SMS}
              contactInfo={redactedPhoneNumber}
              onClick={handleResendSMS}
              initialCountdown={mfaCodeDeliveredTo === MULTI_FACTOR_METHODS.SMS ? MINUTE : 0}
            />
          )}
          {email && email.length > 0 && (
            <ResendCodeButton
              contactMethod={MULTI_FACTOR_METHODS.EMAIL}
              contactInfo={email}
              onClick={handleResendEmail}
              initialCountdown={mfaCodeDeliveredTo === MULTI_FACTOR_METHODS.EMAIL ? MINUTE : 0}
            />
          )}
        </>
      )}
    </div>
  );
};
